import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RESOURCES_API_URL } from "@/config/constants";
import type { ResourceItem } from "@/data/content";
import type { AppLocale } from "@/i18n/constants";
import { resolveResourceIcon } from "@/utils/resourceIcons";

type ApiResource = {
  id: string;
  title: string;
  desc: string;
  details?: string;
  type: string;
  link: string | null;
  downloadAs: string | null;
  coverSrc: string | null;
  actionLabel: string | null;
  icon: string;
  inactive: boolean;
  priceCents?: number;
  currency?: string;
  badge?: string | null;
};

function mapApiResource(item: ApiResource): ResourceItem {
  return {
    id: item.id,
    title: item.title,
    desc: item.desc,
    details: item.details || undefined,
    type: item.type,
    link: item.link ?? undefined,
    downloadAs: item.downloadAs ?? undefined,
    coverSrc: item.coverSrc ?? undefined,
    actionLabel: item.actionLabel ?? undefined,
    icon: resolveResourceIcon(item.icon),
    inactive: item.inactive,
    priceCents: item.priceCents ?? 0,
    currency: item.currency ?? "eur",
    badge: item.badge ?? undefined,
  };
}

/** Loads resources from Supabase via /api/resources. No static fallback. */
export function useSiteResources(): {
  resources: ResourceItem[];
  loading: boolean;
} {
  const { i18n } = useTranslation();
  const locale = (i18n.language === "en" ? "en" : "es") as AppLocale;
  const [resources, setResources] = useState<ResourceItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const res = await fetch(`${RESOURCES_API_URL}?locale=${locale}`);
        if (!res.ok) {
          if (!cancelled) setResources([]);
          return;
        }
        const body = (await res.json()) as { resources?: ApiResource[] };
        if (cancelled) return;
        setResources((body.resources ?? []).map(mapApiResource));
      } catch {
        if (!cancelled) setResources([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  return { resources, loading };
}
